import { Moment } from 'moment-timezone';
import { TFunction } from 'i18next';

interface ZoneSpec {
	/** Translation key for the region word the option leads with. */
	labelKey: string;
	/** Canonical IANA id written into the form's `timezone` field. */
	value: string;
}

/* The same seven zones, in the same west→east order and under the same canonical IANA ids, as
 * downstroke's show-creation picker (`timezones` in
 * packages/venues/src/client/components/tixologi-wizard/utils.ts) — a show reads the same wherever
 * it is created or edited. */
const ZONES: ZoneSpec[] = [
	{ labelKey: 'timezone-hawaii', value: 'Pacific/Honolulu' },
	{ labelKey: 'timezone-alaska', value: 'America/Anchorage' },
	{ labelKey: 'timezone-pacific', value: 'America/Los_Angeles' },
	{ labelKey: 'timezone-phoenix', value: 'America/Phoenix' },
	{ labelKey: 'timezone-mountain', value: 'America/Denver' },
	{ labelKey: 'timezone-central', value: 'America/Chicago' },
	{ labelKey: 'timezone-eastern', value: 'America/New_York' },
];

/* Shows saved while the picker still offered the deprecated `US/*` aliases hold one of these ids.
 * Mapping them onto the canonical id keeps such a show showing its zone instead of a false blank. */
const CANONICAL_BY_ALIAS: Record<string, string> = {
	'US/Hawaii': 'Pacific/Honolulu',
	'US/Alaska': 'America/Anchorage',
	'US/Pacific': 'America/Los_Angeles',
	'US/Arizona': 'America/Phoenix',
	'US/Mountain': 'America/Denver',
	'US/Central': 'America/Chicago',
	'US/Eastern': 'America/New_York',
};

/** Resolve a stored zone id to the one the picker offers. Anything that is not a known alias —
 *  a canonical id, an unknown zone, an empty value — is returned unchanged. */
export function canonicalTimezone(value: string): string;
export function canonicalTimezone(value: string | null | undefined): string | null | undefined;
export function canonicalTimezone(value: string | null | undefined): string | null | undefined {
	if (!value) return value;
	return CANONICAL_BY_ALIAS[value] ?? value;
}

/* The IANA zone id is carried as the option `description` so it is both visible beside the label and
 * matched by the combobox search — typing "denver" finds Mountain, the same way "mountain" does. */
export const timezones = (moment: Moment, t: TFunction) =>
	ZONES.map(({ labelKey, value }) => ({
		label: `${t(labelKey)} (${moment.tz(value).format('zz')})`,
		value,
		description: value,
	}));
